import { useAppStore } from "@/store";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { observer } from "mobx-react-lite";
import {
  MDSSelect,
  MDSIconButton,
  MDSButton,
  MDSButtonIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSBadge,
  MDSBadgeVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSSelectHeader,
  MDSIconButtonVariant,
} from "@/mds";
import { IMDSSelectItemProps } from "@/mds/components/forms/select/select.types";
import { DateTime } from "luxon";
import { Key, useCallback } from "react";
import { MDSBanner } from "@/mds/components/banner/banner";
import { MDSBannerKind, MDSBannerSlotName } from "@/mds/components/banner/banner.types";
import { MdsIconKind } from "@/design-system/components/icon";
import { generateVersionDate } from "@/domains/date/date";
import { Uuid } from "@/domains/global/identifiers";
import { pluralize } from "@/modules/pluralize/pluralize";
import useMeasure from "react-use/lib/useMeasure";
import {
  ItemContainer,
  ItemTitleRow,
  ItemSubtitleRow,
  LeftSlotContainer,
  RightSlotContainer,
  ItemLeft,
  ItemRight,
} from "./version-history.styles";
import { MEM_SPACE_ACCOUNT_ID } from "@/store/note-version-history/constants";

export const NoteVersionHistoryMenuBar = observer(function NoteVersionHistoryMenuBar() {
  const { store } = useAppStore();
  const page = useTabPageStore("notesViewPage");
  const { hasIconsOnly, hasCompactText, hasNoNavButton, containerRef } = useContainerBreakpoints();

  const isActive = typeof page.noteVersionHistory.currentNoteId === "string";
  if (!isActive) return null;

  const noteVersionHistory = page.noteVersionHistory;

  const selectedKey = noteVersionHistory.selectedSnapshot?.id || "";

  const versionItems: IMDSSelectItemProps[] = noteVersionHistory.snapshots.map(s => {
    const editCount = s.edit_count ?? 1;
    const editorId = s.editor_space_account_id;
    const isMe = editorId === store.spaceAccounts.myPersonalSpaceAccountId;
    const isMem = editorId?.toLowerCase() === MEM_SPACE_ACCOUNT_ID.toLowerCase();

    const spaceAccount = editorId
      ? store.spaceAccounts.getSpaceAccount(editorId as unknown as Uuid)
      : undefined;
    const contact = editorId
      ? store.contacts.getBySpaceAccountId(editorId as unknown as Uuid)
      : undefined;
    const authorName = isMe
      ? store.account.myAccount?.displayName || "You"
      : isMem
        ? "Mem"
        : spaceAccount?.profileDisplayName || contact?.profileDisplayName || "";

    const isViewing = s.id === noteVersionHistory.selectedSnapshot?.id;

    const editBy = `${editCount} ${pluralize(editCount, "edit", "edits")} ${
      authorName ? "by " + authorName : ""
    }`;
    const baseDateText = generateVersionDate(DateTime.fromISO(s.created_at), {
      separator: "comma",
      abbreviateMonthWithYear: true,
      compact: hasCompactText,
    });
    const editsOnlyText = `${editCount} ${pluralize(editCount, "edit", "edits")}`;
    const textValue = hasCompactText
      ? `${baseDateText} · ${editsOnlyText}`
      : `${baseDateText} · ${editBy}`;

    const children = (
      <ItemContainer>
        <ItemLeft>
          <ItemTitleRow slot="label">
            <span>{generateVersionDate(DateTime.fromISO(s.created_at), { separator: "dot" })}</span>
          </ItemTitleRow>
          <ItemSubtitleRow slot="description">{editBy}</ItemSubtitleRow>
        </ItemLeft>

        {isViewing && (
          <ItemRight>
            <MDSBadge label="Viewing" variant={MDSBadgeVariant.Emphasis} icon={MdsIconKind.Eye} />
          </ItemRight>
        )}
      </ItemContainer>
    );

    return {
      id: s.id,
      children,
      textValue,
    } as IMDSSelectItemProps;
  });

  const handleVersionSelect = (key: Key | null) => {
    const idx = noteVersionHistory.snapshots.findIndex(s => s.id === key);
    if (idx !== -1) noteVersionHistory.selectNoteSnapshot(idx);
  };

  const handleRestore = async () => {
    await noteVersionHistory.restoreSelectedNoteSnapshot();
  };

  // Selection helpers for up/down version navigation
  const selectedIndex = noteVersionHistory.selectedSnapshot
    ? noteVersionHistory.snapshots.findIndex(s => s.id === noteVersionHistory.selectedSnapshot?.id)
    : -1;
  const canGoNewer = selectedIndex > 0; // index 0 is newest
  const canGoOlder = selectedIndex >= 0 && selectedIndex < noteVersionHistory.snapshots.length - 1;

  const goNewer = () => {
    if (!canGoNewer) return;
    noteVersionHistory.selectNoteSnapshot(selectedIndex - 1);
  };

  const goOlder = () => {
    if (!canGoOlder) return;
    noteVersionHistory.selectNoteSnapshot(selectedIndex + 1);
  };

  return (
    <MDSBanner kind={MDSBannerKind.Info} ref={containerRef}>
      <LeftSlotContainer slot={MDSBannerSlotName.Left}>
        {!hasNoNavButton && (
          <MDSIconButton
            iconKind={MdsIconKind.AngleUpSolid}
            isDisabled={!canGoNewer}
            onClick={goNewer}
            data-test-id="version-history-up-button"
            variant={MDSIconButtonVariant.OnEmphasis}
          />
        )}
        {!hasNoNavButton && (
          <MDSIconButton
            iconKind={MdsIconKind.AngleDownSolid}
            isDisabled={!canGoOlder}
            onClick={goOlder}
            data-test-id="version-history-down-button"
            variant={MDSIconButtonVariant.OnEmphasis}
          />
        )}
        <MDSSelect
          label="Version"
          selectedKey={selectedKey}
          onSelectionChange={handleVersionSelect}
          isDisabled={noteVersionHistory.isLoadingContent}
          items={versionItems}
          popoverProps={{ offset: -4 }}
          buttonVariant={MDSButtonVariant.EmphasisLight}
          renderSelectValue={props => {
            return (
              <MDSText
                variant={MDSTextVariant.TextSmd}
                color={MDSTextColor.Emphasis}
                weight={MDSTextWeight.Smedium}
              >
                {props.selectedText}
              </MDSText>
            );
          }}
        >
          <MDSSelectHeader label="Version history" />
        </MDSSelect>
      </LeftSlotContainer>
      <RightSlotContainer slot={MDSBannerSlotName.Right}>
        <MDSButton
          onClick={handleRestore}
          isDisabled={!noteVersionHistory.canRestore}
          isLoading={noteVersionHistory.isLoadingContent}
          variant={MDSButtonVariant.EmphasisLight}
          aria-label="Restore version"
        >
          <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.History} />
          {!hasIconsOnly && "Restore version"}
        </MDSButton>
        <MDSButton
          onClick={() => noteVersionHistory.closeNoteVersionHistory()}
          variant={MDSButtonVariant.EmphasisLight}
          aria-label="Return to editing"
        >
          <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.Exit} />
          {!hasIconsOnly && "Return to editing"}
        </MDSButton>
      </RightSlotContainer>
    </MDSBanner>
  );
});

const useContainerBreakpoints = () => {
  const MEDIUM_BP = 785; // right-side buttons become icon-only below this
  const SMALL_BP = 600; // select label becomes compact below this
  const MOBILE_BP = 420; // hide up/down history nav below this

  const [measureRef, { width: measuredWidth }] = useMeasure<HTMLDivElement>();
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) measureRef(node);
    },
    [measureRef]
  );

  const hasIconsOnly = measuredWidth > 0 && measuredWidth < MEDIUM_BP;
  const hasCompactText = measuredWidth > 0 && measuredWidth < SMALL_BP;
  const hasNoNavButton = measuredWidth > 0 && measuredWidth < MOBILE_BP;

  return {
    hasIconsOnly,
    hasCompactText,
    hasNoNavButton,
    containerRef,
  };
};
